/**
 * TopHud.ts
 * Mobile-first persistent Top HUD DOM component for American Dream: Pocket Life.
 * Renders cash balance with floating deltas, Energy, Hunger, and Happiness bars with mood emojis,
 * active buff chips with mm:ss countdowns, and clickable career title pill.
 */

import { EventBus } from '../core/EventBus';
import { SurvivalStats, ActiveBuff } from '../core/Types';
import {
  UI_ICONS,
  getMoodGouacheIconHtml,
  getTimeOfDayBadgeInfo,
  getFoodIconSvg,
  renderUiGouacheIcon,
} from './Icons';
import { preventButtonFocusScroll } from './ScrollPreserver';

export class TopHud {
  private container: HTMLElement;
  private moneyValEl!: HTMLElement;
  private floatingDeltasEl!: HTMLElement;
  private careerTitleEl!: HTMLElement;
  private careerPillEl!: HTMLElement;
  private timePillEl!: HTMLElement;

  private energyValEl!: HTMLElement;
  private energyBarEl!: HTMLElement;

  private hungerValEl!: HTMLElement;
  private hungerBarEl!: HTMLElement;

  private happinessValEl!: HTMLElement;
  private happinessBarEl!: HTMLElement;
  private happinessMoodEl!: HTMLElement;

  private buffsRibbonEl!: HTMLElement;

  private currentMoney: number = 0;
  private activeBuffs: ActiveBuff[] = [];
  private buffTimerInterval: ReturnType<typeof setInterval> | null = null;
  private currentDisplayHour: number = new Date().getHours();
  private lastRealHour: number = new Date().getHours();

  constructor(containerId: string = 'top-hud-container') {
    const el = document.getElementById(containerId) || document.getElementById('top-hud-bar');
    if (!el) {
      throw new Error(`[TopHud] Mount element #${containerId} not found`);
    }
    this.container = el;
    this.renderInitialStructure();
    this.bindEvents();
    this.startBuffTicker();
  }

  private renderInitialStructure(): void {
    const timeInfo = getTimeOfDayBadgeInfo(this.currentDisplayHour);

    this.container.innerHTML = `
      <div class="hud-meta-row">
        <div class="hud-brand-crest" title="American Dream: Pocket Life" aria-label="Pocket Star Diorama Crest">
          ${renderUiGouacheIcon('icon_home_cottage', { size: 23, className: 'hud-crest-png' })}
        </div>

        <div id="hud-money-badge" class="hud-badge money-badge" aria-label="Tài khoản tiền mặt">
          <span class="icon">${renderUiGouacheIcon('icon_cash_wallet', { size: 19, className: 'hud-gouache-badge-img' })}</span>
          <span id="hud-money-val" class="val tabular-nums">$0</span>
          <div id="hud-floating-deltas" class="floating-deltas-container"></div>
        </div>

        <div id="hud-time-pill" class="hud-time-pill ${timeInfo.phaseClass}" role="button" tabindex="0" title="Chạm để đổi ánh sáng thời gian trong phòng" aria-label="Thời gian trong ngày">
          <span class="time-icon">${timeInfo.iconHtml}</span>
          <span class="time-label">${timeInfo.label}</span>
        </div>
        
        <button type="button" id="hud-career-pill" class="hud-career-pill" aria-label="Nghề nghiệp hiện tại">
          <span class="badge-icon">${renderUiGouacheIcon('icon_career_briefcase', { size: 19, className: 'hud-gouache-badge-img' })}</span>
          <span id="hud-career-title" class="title">Đang tìm việc</span>
        </button>
      </div>

      <div class="hud-stats-grid">
        <div class="stat-col stat-col-interactive" id="hud-stat-energy" role="button" tabindex="0" title="Chạm để đi tới Giường ngủ hồi Năng lượng">
          <div class="stat-label-row">
            <span class="stat-icon">${renderUiGouacheIcon('icon_energy_bolt', { size: 17, className: 'hud-gouache-stat-img' })}</span>
            <span class="stat-title">Năng lượng</span>
            <span id="hud-energy-val" class="stat-num tabular-nums">100%</span>
          </div>
          <div class="progress-track">
            <div id="hud-energy-bar" class="progress-fill stage-green" style="width: 100%;"></div>
          </div>
        </div>

        <div class="stat-col stat-col-interactive" id="hud-stat-hunger" role="button" tabindex="0" title="Chạm để đi tới Tủ lạnh ăn uống">
          <div class="stat-label-row">
            <span class="stat-icon">${renderUiGouacheIcon('icon_pantry_platter', { size: 18, className: 'hud-gouache-stat-img' })}</span>
            <span class="stat-title">Cơn đói</span>
            <span id="hud-hunger-val" class="stat-num tabular-nums">100%</span>
          </div>
          <div class="progress-track">
            <div id="hud-hunger-bar" class="progress-fill stage-green" style="width: 100%;"></div>
          </div>
        </div>

        <div class="stat-col stat-col-interactive" id="hud-stat-happiness" role="button" tabindex="0" title="Chạm để đi tới Ghế Sofa thư giãn">
          <div class="stat-label-row">
            <span id="hud-happiness-mood" class="stat-icon mood-icon">${getMoodGouacheIconHtml(100, 17)}</span>
            <span class="stat-title">Tâm trạng</span>
            <span id="hud-happiness-val" class="stat-num tabular-nums">100%</span>
          </div>
          <div class="progress-track">
            <div id="hud-happiness-bar" class="progress-fill stage-ecstatic" style="width: 100%;"></div>
          </div>
        </div>
      </div>

      <div id="hud-buffs-ribbon" class="hud-buffs-ribbon" aria-label="Hiệu ứng tăng lực"></div>
    `;

    // Cache elements
    this.moneyValEl = this.container.querySelector('#hud-money-val')!;
    this.floatingDeltasEl = this.container.querySelector('#hud-floating-deltas')!;
    this.careerTitleEl = this.container.querySelector('#hud-career-title')!;
    this.careerPillEl = this.container.querySelector('#hud-career-pill')!;
    this.timePillEl = this.container.querySelector('#hud-time-pill')!;

    this.energyValEl = this.container.querySelector('#hud-energy-val')!;
    this.energyBarEl = this.container.querySelector('#hud-energy-bar')!;

    this.hungerValEl = this.container.querySelector('#hud-hunger-val')!;
    this.hungerBarEl = this.container.querySelector('#hud-hunger-bar')!;

    this.happinessValEl = this.container.querySelector('#hud-happiness-val')!;
    this.happinessBarEl = this.container.querySelector('#hud-happiness-bar')!;
    this.happinessMoodEl = this.container.querySelector('#hud-happiness-mood')!;

    this.buffsRibbonEl = this.container.querySelector('#hud-buffs-ribbon')!;

    preventButtonFocusScroll(this.container);

    // Career pill click handler
    this.careerPillEl.addEventListener('click', () => {
      EventBus.emit('OPEN_CAREER_HUB');
    });

    // Time-of-day pill click handler: cycles Day (10) -> Sunset (18) -> Night (22)
    this.timePillEl.addEventListener('click', () => {
      let nextHour = 10;
      if (this.currentDisplayHour >= 6 && this.currentDisplayHour < 17) {
        nextHour = 18;
      } else if (this.currentDisplayHour >= 17 && this.currentDisplayHour < 20) {
        nextHour = 22;
      } else {
        nextHour = 10;
      }
      EventBus.emit('TIME_ADVANCED', nextHour);
    });

    // Survival stat bar quick-interact handlers (tap Energy -> Bed, Hunger -> Fridge, Happiness -> Sofa)
    const energyCol = this.container.querySelector('#hud-stat-energy');
    const hungerCol = this.container.querySelector('#hud-stat-hunger');
    const happinessCol = this.container.querySelector('#hud-stat-happiness');

    energyCol?.addEventListener('click', () => {
      EventBus.emit('INTERACT_FURNITURE_TYPE', 'bed');
    });
    hungerCol?.addEventListener('click', () => {
      EventBus.emit('INTERACT_FURNITURE_TYPE', 'fridge');
    });
    happinessCol?.addEventListener('click', () => {
      EventBus.emit('INTERACT_FURNITURE_TYPE', 'sofa');
    });
  }

  private bindEvents(): void {
    EventBus.on('STATS_UPDATED', (payload: { stats?: SurvivalStats } & Partial<SurvivalStats>) => {
      const nextStats = payload?.stats ?? (payload as SurvivalStats);
      if (nextStats && typeof nextStats.energy === 'number') {
        this.updateStats(nextStats);
      }
    });

    EventBus.on('MONEY_CHANGED', ({ current, delta }: { current: number; delta: number }) => {
      this.updateMoney(current, delta);
    });

    EventBus.on('BUFFS_UPDATED', (buffs: ActiveBuff[]) => {
      this.activeBuffs = [...buffs];
      this.renderBuffChips();
    });

    EventBus.on('CAREER_UPDATED', ({ title }: { title: string }) => {
      this.updateCareerTitle(title);
    });

    EventBus.on('TIME_ADVANCED', (overrideHour?: number) => {
      this.updateTimeOfDayBadge(overrideHour);
    });
  }

  public updateTimeOfDayBadge(overrideHour?: number): void {
    if (!this.timePillEl) return;
    if (typeof overrideHour === 'number' && !Number.isNaN(overrideHour)) {
      this.currentDisplayHour = overrideHour;
    } else {
      this.currentDisplayHour = new Date().getHours();
    }
    const info = getTimeOfDayBadgeInfo(this.currentDisplayHour);
    this.timePillEl.className = `hud-time-pill ${info.phaseClass}`;
    this.timePillEl.innerHTML = `
      <span class="time-icon">${info.iconHtml}</span>
      <span class="time-label">${info.label}</span>
    `;
  }

  public updateStats(stats: SurvivalStats): void {
    // 1. Energy update
    const energy = Math.round(stats.energy);
    this.energyValEl.textContent = `${energy}%`;
    this.energyBarEl.style.width = `${energy}%`;
    this.energyBarEl.className = 'progress-fill ' + this.getEnergyStageClass(energy);

    // 2. Hunger update
    const hunger = Math.round(stats.hunger);
    this.hungerValEl.textContent = `${hunger}%`;
    this.hungerBarEl.style.width = `${hunger}%`;
    this.hungerBarEl.className = 'progress-fill ' + this.getHungerStageClass(hunger);

    // 3. Happiness update
    const happiness = Math.round(stats.happiness);
    this.happinessValEl.textContent = `${happiness}%`;
    this.happinessBarEl.style.width = `${happiness}%`;
    this.happinessBarEl.className = 'progress-fill ' + this.getHappinessStageClass(happiness);
    this.happinessMoodEl.innerHTML = getMoodGouacheIconHtml(happiness, 17);

    // Update money if changed
    if (this.currentMoney !== stats.money) {
      this.updateMoney(stats.money, stats.money - this.currentMoney);
    }
  }

  public updateMoney(amount: number, delta: number = 0): void {
    this.currentMoney = amount;
    this.moneyValEl.textContent = `$${amount.toLocaleString('en-US')}`;

    if (delta !== 0) {
      this.spawnFloatingDelta(delta);
    }
  }

  private spawnFloatingDelta(delta: number): void {
    const isPositive = delta > 0;
    const sign = isPositive ? '+' : '-';
    const deltaEl = document.createElement('span');
    deltaEl.className = `floating-delta tabular-nums ${isPositive ? 'delta-pos' : 'delta-neg'}`;
    deltaEl.textContent = `${sign} $${Math.abs(delta).toLocaleString('en-US')}`;

    this.floatingDeltasEl.appendChild(deltaEl);
    setTimeout(() => {
      if (deltaEl.parentNode) {
        deltaEl.parentNode.removeChild(deltaEl);
      }
    }, 1200);
  }

  public updateCareerTitle(title: string): void {
    this.careerTitleEl.textContent = title || 'Đang tìm việc';
  }

  private getEnergyStageClass(val: number): string {
    if (val > 69) return 'stage-green';
    if (val >= 30) return 'stage-yellow';
    if (val > 10) return 'stage-orange';
    return 'stage-red pulse-alert';
  }

  private getHungerStageClass(val: number): string {
    if (val > 60) return 'stage-green';
    if (val >= 30) return 'stage-yellow';
    if (val > 5) return 'stage-orange';
    return 'stage-red rumble-shake';
  }

  private getHappinessStageClass(val: number): string {
    if (val > 70) return 'stage-ecstatic';
    if (val >= 40) return 'stage-content';
    if (val > 15) return 'stage-low';
    return 'stage-depressed';
  }

  private startBuffTicker(): void {
    if (this.buffTimerInterval !== null) {
      clearInterval(this.buffTimerInterval);
    }
    this.buffTimerInterval = setInterval(() => {
      if (typeof document !== 'undefined' && (!this.container || !document.body.contains(this.container))) {
        this.destroy();
        return;
      }
      this.refreshBuffCountdowns();
      const realHour = new Date().getHours();
      if (realHour !== this.lastRealHour) {
        this.lastRealHour = realHour;
        EventBus.emit('TIME_ADVANCED', realHour);
      }
    }, 1000);
  }

  private renderBuffChips(): void {
    this.buffsRibbonEl.innerHTML = '';
    const now = Date.now();

    for (const buff of this.activeBuffs) {
      const remainingSec = Math.max(0, Math.floor((buff.expiresAt - now) / 1000));
      const chip = document.createElement('div');
      chip.className = 'buff-chip';
      chip.dataset.buffId = buff.id;

      let icon = buff.icon || this.getBuffDefaultIcon(buff.type);
      if (typeof icon === 'string' && icon.startsWith('food_')) {
        icon = getFoodIconSvg(icon, { size: 15 });
      } else if (icon === '👑' || buff.id === 'buff_bed_t3_refresh') {
        icon = renderUiGouacheIcon('icon_trophy_medal', { size: 15 });
      }

      chip.innerHTML = `
        <span class="buff-icon" style="display: inline-flex; align-items: center; justify-content: center; width: 15px; height: 15px; flex-shrink: 0;">${icon}</span>
        <span class="buff-name">${buff.name}</span>
        <span class="buff-timer tabular-nums">${this.formatCountdown(remainingSec)}</span>
      `;

      chip.addEventListener('click', () => {
        const liveRemainingSec = Math.max(0, Math.floor((buff.expiresAt - Date.now()) / 1000));
        EventBus.emit('SHOW_TOAST', {
          message: `${buff.name}: ${buff.description} (${this.formatCountdown(liveRemainingSec)})`,
          type: 'info'
        });
      });

      this.buffsRibbonEl.appendChild(chip);
    }
  }

  private refreshBuffCountdowns(): void {
    const now = Date.now();
    const chips = this.buffsRibbonEl.querySelectorAll<HTMLElement>('.buff-chip');

    chips.forEach((chip) => {
      const buffId = chip.dataset.buffId;
      const buff = this.activeBuffs.find((b) => b.id === buffId);
      if (buff) {
        const remainingSec = Math.max(0, Math.floor((buff.expiresAt - now) / 1000));
        const timerEl = chip.querySelector('.buff-timer');
        if (timerEl) {
          timerEl.textContent = this.formatCountdown(remainingSec);
        }
      }
    });
  }

  private formatCountdown(totalSec: number): string {
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private getBuffDefaultIcon(type: string): string {
    switch (type) {
      case 'PAY_BOOST': return renderUiGouacheIcon('icon_cash_wallet', { size: 15 });
      case 'ENERGY_SAVE': return renderUiGouacheIcon('icon_energy_bolt', { size: 15 });
      case 'STUDY_BOOST': return UI_ICONS.academic;
      case 'HAPPINESS_SHIELD': return renderUiGouacheIcon('icon_mood_happy', { size: 15 });
      case 'LOCAL_VIBE': return renderUiGouacheIcon('icon_atlas_compass', { size: 15 });
      default: return renderUiGouacheIcon('icon_pantry_platter', { size: 15 });
    }
  }

  public destroy(): void {
    if (this.buffTimerInterval !== null) {
      clearInterval(this.buffTimerInterval);
      this.buffTimerInterval = null;
    }
  }
}
